import * as Dfns from 'date-fns/fp';
import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import {
  MdAdd,
  MdOutlineFileDownload,
  MdOutlineReplay,
  MdOutlineUploadFile,
  MdSave
} from 'react-icons/md';
import { useQueryClient } from '@tanstack/react-query';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button/Button';
import { BPMTableWidget } from '@Components/BPMTableWidget';
import { Appearance, Intent, Size } from '@Components/designConstants';
import { Checkbox } from '@Components/FormCheck';
import { IconButton } from '@Components/IconButton';
import { BPMTable } from '@Components/StickyTable';
import { TableLoading } from '@Components/TableEmptyState';
import { useAppSelector } from '@src/redux';
import { useSetError } from '@src/redux/modals';
import { emailSelector } from '@src/redux/user';
import { writeWorkbook } from '@src/utils/download-utils';
import {
  BULK_ALERT_TITLE,
  EMPTY_OPTIONS,
  GoalRow,
  HideAlertKey,
  HideAlertKeyType,
  NO_SEGMENTS
} from './goal-intake-constants';
import {
  diffDrafts,
  DraftRow,
  TableItem,
  toTableItems,
  getLegacyErrors,
  getRowErrors,
  GoalView,
  GoalViewType,
  groupDrafts,
  NEW_ROW_KEY_PREFIX,
  toDrafts
} from './goal-intake-edit-utils';
import {
  buildGoalsWorkbook,
  isLegacyConflict,
  processBulkImport,
  toLegacyBooking,
  toSaveRow
} from './goal-intake-utils';
import { buildGoalHeaders } from './GoalIntakeColumns';
import { GoalIntakeChannelView } from './GoalIntakeChannelView';
import { GoalIntakeViewToggle } from './GoalIntakeViewToggle';
import {
  goalRowsQueryKey,
  saveGoals,
  saveLegacyBookings,
  useCustomSegments,
  useGoalRows,
  useGoalSelectorOptions
} from './use-goal-intake';

// Browser storage can be unavailable (private mode, blocked site data); treat that as "not
// dismissed" so the alert simply shows again.
const isAlertHidden = (key: HideAlertKeyType): boolean => {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

const hideAlert = (key: HideAlertKeyType): void => {
  try {
    localStorage.setItem(key, 'true');
  } catch {
    // Nothing to persist to; the alert will show again next time.
  }
};

const NO_ROWS: GoalRow[] = [];
const ROW_HEIGHT = 48;
const MARKER_ROW_HEIGHT = 32;
const CHIP_LINE_HEIGHT = 32;
// Roughly how many characters of chips fit on one line of a multi-select cell.
const CHIP_LINE_CHARS = 32;

// Multi-select cells wrap their chips onto more lines, so the row grows to fit the longest one.
const getRowHeight = (item: TableItem) => {
  if (item.markerLabel !== undefined) return MARKER_ROW_HEIGHT;
  const chipLines = Object.values(item.row)
    .filter(Array.isArray)
    .map(values =>
      Math.ceil(values.reduce((sum, value) => sum + String(value).length + 4, 0) / CHIP_LINE_CHARS)
    );
  return Math.max(ROW_HEIGHT, Math.max(0, ...chipLines) * CHIP_LINE_HEIGHT + 16);
};

interface GoalIntakeGoalsProps {
  company: string;
}

export const GoalIntakeGoals = ({ company }: GoalIntakeGoalsProps) => {
  const setError = useSetError();
  const queryClient = useQueryClient();
  const email = useAppSelector(emailSelector);
  const newRowCount = useRef(0);

  const {
    data: allRows = NO_ROWS,
    isLoading: rowsLoading,
    error: rowsError,
    refetch
  } = useGoalRows(company);
  const { data: customSegments = NO_SEGMENTS, error: segmentsError } = useCustomSegments(company);
  const {
    data: selectorOptions = EMPTY_OPTIONS,
    isLoading: optionsLoading,
    error: optionsError
  } = useGoalSelectorOptions(company);

  // Legacy expected_bookings rows are edited in place but saved to expected_bookings; the Goal
  // Intake save, validation and downloads only see the other rows. A legacy row is flagged when a
  // saved row overlaps it for the same goal and Channel (the legacy value still wins there).
  const rows = useMemo(() => allRows.filter(row => !row.legacy), [allRows]);
  const legacyRows = useMemo(() => allRows.filter(row => row.legacy), [allRows]);
  const [drafts, setDrafts] = useState<DraftRow[]>([]);
  const [legacyDrafts, setLegacyDrafts] = useState<DraftRow[]>([]);
  const [view, setView] = useState<GoalViewType>(GoalView.MONTH);
  const [isSaving, setIsSaving] = useState(false);
  const [bulkAlert, setBulkAlert] = useState<HideAlertKeyType | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [bulkImportError, setBulkImportError] = useState<string | null>(null);

  // Saved rows (first load, company switch, after a save) replace the working copy.
  useEffect(() => {
    setDrafts(toDrafts(rows));
  }, [rows]);
  useEffect(() => {
    setLegacyDrafts(toDrafts(legacyRows));
  }, [legacyRows]);

  const loadError = rowsError ?? segmentsError ?? optionsError;
  useEffect(() => {
    if (loadError) {
      setError({
        message: `Failed to get Goal Intake data. ERROR: ${loadError.message}`,
        reportError: loadError
      });
    }
  }, [loadError, setError]);

  const diff = useMemo(() => diffDrafts(drafts, rows), [drafts, rows]);
  const legacyDiff = useMemo(
    () => diffDrafts(legacyDrafts, legacyRows),
    [legacyDrafts, legacyRows]
  );
  const rowErrors = useMemo(
    () =>
      new Map([
        ...getRowErrors(drafts, diff.changedKeys, customSegments, selectorOptions),
        ...getLegacyErrors(legacyDrafts, legacyDiff.changedKeys)
      ]),
    [
      drafts,
      diff.changedKeys,
      legacyDrafts,
      legacyDiff.changedKeys,
      customSegments,
      selectorOptions
    ]
  );
  const unsavedKeys = useMemo(
    () => new Set([...diff.changedKeys, ...legacyDiff.changedKeys]),
    [diff.changedKeys, legacyDiff.changedKeys]
  );
  const allDrafts = useMemo(
    () => [
      ...drafts,
      ...legacyDrafts.map(draft => ({
        ...draft,
        row: {
          ...draft.row,
          legacyConflict: rows.some(other => isLegacyConflict(draft.row, other))
        }
      }))
    ],
    [drafts, legacyDrafts, rows]
  );
  const tableItems = useMemo(
    () => (view === GoalView.CHANNEL ? [] : toTableItems(groupDrafts(allDrafts, view))),
    [allDrafts, view]
  );

  const unsavedCount =
    diff.insert.length +
    diff.update.length +
    diff.delete.length +
    legacyDiff.update.length +
    legacyDiff.delete.length;

  // Keys never collide: new rows are `new-N`, saved rows positive ids, legacy rows negative ids.
  // Edits are ignored while saving: the refetch after the save replaces every draft.
  const updateDraft = useCallback(
    (key: string, row: GoalRow) => {
      if (isSaving) return;
      // legacyConflict is display-only; keeping it on the row would make a reverted edit look changed.
      const clean = R.omit(['legacyConflict'], row);
      const apply = (prev: DraftRow[]) =>
        prev.map(draft => (draft.key === key ? { ...draft, row: clean } : draft));
      setDrafts(apply);
      setLegacyDrafts(apply);
    },
    [isSaving]
  );

  const deleteDraft = useCallback(
    (key: string) => {
      if (isSaving) return;
      const remove = (prev: DraftRow[]) => prev.filter(draft => draft.key !== key);
      setDrafts(remove);
      setLegacyDrafts(remove);
    },
    [isSaving]
  );

  const headers = useMemo(
    () =>
      buildGoalHeaders({
        customSegments,
        selectorOptions,
        unsavedKeys,
        errors: rowErrors,
        onChange: updateDraft,
        onDelete: deleteDraft
      }),
    [customSegments, selectorOptions, unsavedKeys, rowErrors, updateDraft, deleteDraft]
  );

  const addDraft = (row: GoalRow): string => {
    if (isSaving) return '';
    const key = `${NEW_ROW_KEY_PREFIX}${newRowCount.current++}`;
    setDrafts(prev => [...prev, { key, row }]);
    return key;
  };

  const addDrafts = (newRows: GoalRow[]) => {
    if (isSaving) return;
    setDrafts(prev => [
      ...prev,
      ...newRows.map(row => ({ key: `${NEW_ROW_KEY_PREFIX}${newRowCount.current++}`, row }))
    ]);
  };

  const renderNoRows = useCallback(
    () => <TableLoading isLoading={rowsLoading} error={rowsError} headerHeight={0} />,
    [rowsLoading, rowsError]
  );

  const save = async () => {
    if (rowErrors.size > 0) {
      setError({
        message: `${rowErrors.size} row${rowErrors.size === 1 ? '' : 's'} need fixing before saving.`
      });
      return;
    }
    const context = { company, email, customSegments };
    const lastmodified = Dfns.formatISO(new Date());
    setIsSaving(true);
    try {
      if (diff.insert.length + diff.update.length + diff.delete.length > 0) {
        await saveGoals({
          insert: diff.insert.map(row =>
            toSaveRow({ ...row, id: undefined, lastmodified }, context)
          ),
          update: diff.update.map(row => toSaveRow({ ...row, lastmodified }, context)),
          delete: diff.delete.map(row => toSaveRow(row, context)),
          company,
          customSegments
        });
      }
      if (legacyDiff.update.length + legacyDiff.delete.length > 0) {
        await saveLegacyBookings({
          insert: [],
          update: legacyDiff.update.map(toLegacyBooking),
          delete: legacyDiff.delete.map(toLegacyBooking)
        });
      }
    } catch (e) {
      setError({
        message: `Failed to set Goal Intake data. ERROR: ${e.message}`,
        reportError: e
      });
    } finally {
      // Also after a failed second step, so a retry diffs against the goals that did save.
      await queryClient.invalidateQueries({ queryKey: goalRowsQueryKey(company) });
      setIsSaving(false);
    }
  };

  const downloadGoals = async () => {
    try {
      await writeWorkbook(
        buildGoalsWorkbook(rows, company, customSegments, selectorOptions),
        `${company}.xlsx`
      );
    } catch (e) {
      setError({ message: `Failed to download goals. ERROR: ${e.message}`, reportError: e });
    }
  };

  // Stages the upload's new/changed rows as unsaved edits; nothing is saved until Save.
  const bulkImport = async (file: ArrayBuffer) => {
    try {
      // Diff against freshly fetched rows; a failed refetch goes to the outer catch.
      const { data: freshRows = [] } = await refetch({ throwOnError: true });
      let importedRows: GoalRow[];
      try {
        importedRows = await processBulkImport(
          file,
          freshRows.filter(row => !row.legacy),
          company,
          customSegments,
          selectorOptions
        );
      } catch (validationError) {
        setBulkImportError(validationError.message);
        return;
      }
      if (importedRows.length === 0) {
        setBulkImportError('No updated rows or new changes');
        return;
      }
      const changedById = new Map(
        importedRows.filter(row => row.id != null).map(row => [String(row.id), row])
      );
      setDrafts(prev =>
        prev.map(draft => ({ ...draft, row: changedById.get(draft.key) ?? draft.row }))
      );
      addDrafts(importedRows.filter(row => row.id == null));
    } catch (e) {
      setError({ message: `Failed to import changes ${e.message}`, reportError: e });
    }
  };

  const runBulkAction = (key: HideAlertKeyType) => {
    if (key === HideAlertKey.DOWNLOAD) {
      downloadGoals();
    } else {
      setShowImportModal(true);
    }
  };

  const requestBulkAction = (key: HideAlertKeyType) => {
    if (isAlertHidden(key)) {
      runBulkAction(key);
    } else {
      setBulkAlert(key);
    }
  };

  return (
    <div className='goal-intake-new-goals'>
      <BPMTableWidget
        className='goal-intake-new-table'
        dataTestId='goal-intake-new-table'
        header={
          <div className='goal-intake-new-toolbar'>
            <GoalIntakeViewToggle view={view} onChange={setView} />
          </div>
        }
        rightActions={
          <div className='goal-intake-new-toolbar'>
            <Button
              size={Size.LG}
              appearance={Appearance.OUTLINED}
              leadingElement={<MdOutlineUploadFile />}
              data-testid='goal-intake-new-bulk-upload'
              disabled={optionsLoading || isSaving}
              onClick={() => requestBulkAction(HideAlertKey.BULK_UPLOAD)}
            >
              Bulk Upload
            </Button>
            <IconButton
              appearance={Appearance.OUTLINED}
              tooltip={{ text: 'Download goals' }}
              data-testid='goal-intake-new-download'
              disabled={optionsLoading}
              onClick={() => requestBulkAction(HideAlertKey.DOWNLOAD)}
            >
              <MdOutlineFileDownload />
            </IconButton>
            <Button
              size={Size.LG}
              leadingElement={<MdAdd />}
              data-testid='goal-intake-new-add-row'
              loading={optionsLoading}
              disabled={isSaving}
              onClick={() => addDrafts([{}])}
            >
              Add New Budget / Goal
            </Button>
          </div>
        }
        footer={
          unsavedCount > 0 && (
            <div className='goal-intake-new-footer'>
              <Button
                size={Size.LG}
                appearance={Appearance.EMPTY}
                design={Intent.ERROR}
                leadingElement={<MdOutlineReplay />}
                disabled={isSaving}
                onClick={() => {
                  setDrafts(toDrafts(rows));
                  setLegacyDrafts(toDrafts(legacyRows));
                }}
              >
                Revert All Changes
              </Button>
              <div className='goal-intake-new-footer-save'>
                <span
                  className='goal-intake-new-unsaved-count'
                  data-testid='goal-intake-new-unsaved-count'
                >
                  {unsavedCount} unsaved change{unsavedCount === 1 ? '' : 's'}
                </span>
                <Button
                  size={Size.LG}
                  leadingElement={<MdSave />}
                  loading={isSaving}
                  onClick={save}
                >
                  Save
                </Button>
              </div>
            </div>
          )
        }
      >
        {view === GoalView.CHANNEL ? (
          <GoalIntakeChannelView
            company={company}
            drafts={allDrafts}
            unsavedKeys={unsavedKeys}
            errors={rowErrors}
            onChange={updateDraft}
            onDelete={deleteDraft}
            onAdd={addDraft}
          />
        ) : (
          <BPMTable
            data={tableItems}
            headers={headers}
            filterBar={false}
            alternateColors={false}
            rowHeightGetter={getRowHeight}
            noRowsRenderer={renderNoRows}
          />
        )}
      </BPMTableWidget>
      {bulkAlert && (
        <BulkAlertDialog
          alertKey={bulkAlert}
          onConfirm={() => runBulkAction(bulkAlert)}
          onHide={() => setBulkAlert(null)}
        />
      )}
      {showImportModal && (
        <BulkImportModal importFile={bulkImport} onHide={() => setShowImportModal(false)} />
      )}
      <AlertDialog
        show={bulkImportError !== null}
        onHide={() => setBulkImportError(null)}
        intent={Intent.ERROR}
        title='Bulk Upload Failed'
        dataTestId='goal-intake-new-bulk-error-dialog'
        body={bulkImportError ?? ''}
      />
    </div>
  );
};

interface BulkAlertDialogProps {
  alertKey: HideAlertKeyType;
  onConfirm: () => void;
  onHide: () => void;
}

const BulkAlertDialog = ({ alertKey, onConfirm, onHide }: BulkAlertDialogProps) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const close = () => {
    if (dontShowAgain) {
      hideAlert(alertKey);
    }
    onHide();
  };

  return (
    <AlertDialog
      show
      onHide={close}
      title={BULK_ALERT_TITLE[alertKey]}
      dataTestId='goal-intake-new-bulk-alert-dialog'
      body={
        <div className='goal-intake-new-bulk-alert'>
          <p>
            <b>For New Budgets/Goal:</b> Only define the segment dimensions you will actively pace
            against. To match budgets/goals to spending, the segments that you upload budgets/goals
            for must also exist in your campaign data.
          </p>
          <p>
            <b>For Editing Budgets/Goal:</b> To update a budget, you MUST include its ID in the
            upload sheet. ID can be found in column A of the bulk sheet. Without an ID, a new
            budgets/goals will be created instead.
          </p>
          <Checkbox
            // Form.Check only links the label to its input (so clicking the text toggles it) with an id.
            id='goal-intake-new-hide-bulk-alert'
            label="Don't show this message again"
            checked={dontShowAgain}
            onChange={e => setDontShowAgain(e.target.checked)}
          />
        </div>
      }
      primaryAction={{ onClick: onConfirm, label: 'Confirm' }}
      secondaryAction={{ onClick: () => {}, label: 'Close' }}
    />
  );
};

interface BulkImportModalProps {
  importFile: (file: ArrayBuffer) => Promise<void>;
  onHide: () => void;
}

const BulkImportModal = ({ importFile, onHide }: BulkImportModalProps) => {
  const [file, setFile] = useState<File>();
  const [importing, setImporting] = useState(false);

  const onImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      await importFile(await file.arrayBuffer());
    } finally {
      setImporting(false);
      onHide();
    }
  };

  return (
    <Modal
      show
      onHide={onHide}
      className='goal-intake-new-import-modal'
      data-testid='goal-intake-new-import-modal'
    >
      <Modal.Header closeButton>
        <Modal.Title>Bulk Import (from Excel)</Modal.Title>
      </Modal.Header>
      <Modal.Body className='goal-intake-new-import-body'>
        <div>
          During bulk import rows, duplicates are ignored, changes are applied to existing rows and
          new rows are inserted. Leave id column blank for new rows.
        </div>
        <Form.Control
          type='file'
          accept='.xlsx'
          data-testid='goal-intake-new-import-file'
          onChange={e => {
            if (e.target instanceof HTMLInputElement) {
              setFile(e.target.files?.[0]);
            }
          }}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button disabled={!file} loading={importing} onClick={onImport}>
          Import
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
